"use client";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import DateTimeCard, { roundUpToSlotBoundary, rollStaleStartForward, isBookableAt, firstBookableSlot } from "@/components/DateTimeCard/DateTimeCard";
import useCityTimings from "@/components/SearchForm/useCityTimings";
import dayjs from "dayjs";
import { showDateTime, getFinalVehicleList } from "@/data/staticMethod";
import IMAGES from "@/data/assetData";
import { useRouter, usePathname } from "next/navigation";
import { useSelector, useDispatch } from "react-redux";
import {
  setCityname,
  setDuration,
  getAllVehicles,
  setEmptyCartData,
  getTempDateTime,
  getStartDate,
  getEndDate,
  setSearchType,
  setSearchAreas,
  setSelectedAreas,
  setSearchBrands,
  setSelectedBrands,
  setSearchFuelTypes,
  setSelectedFuelTypes,
  setSearchTransmissions,
  setSelectedTransmissions,
  setSortBy,
} from "@/redux/search/searchSlice";
import { usefetchDataFromApi } from "@/utils/api";
import handleApiError from "@/utils/handleApiError";
import { bumpCartVersion } from "@/redux/order/orderSlice";
import { longTermDuration } from "@/data/footerLinkData";
import Image from "next/image";
import { getCurrentPosition, detectCityByCoordinates, detectCityByIP } from "@/utils/locationHelper";
import { toast } from "react-toastify";
import Button from "@/components/ui/Button";
import {
  usePartner,
  withPartnerFilter,
  syncPartnerBookingOrigin,
  partnerCityList,
} from "@/utils/partner/PartnerContext";

const SearchFormDesktop = ({ type, seoCity = null }) => {
  const showHideRef = useRef();
  const pathname = usePathname();
  const router = useRouter();
  const dispatch = useDispatch();
  // Active partner storefront (null on the global sukuto.com site), used to
  // scope search to the partner's inventory and keep navigation on /partner/<slug>/.
  const partner = usePartner();
  const searchSelector = useSelector((state) => state.search);

  useCityTimings();
  // Only trust the hours when they are stamped with the city we are showing.
  // Sibling effects in one commit share a render snapshot, so during a switch the
  // previous city's hours are still in scope when autoSetDates fires — that rolled
  // a 1am Bangalore pickup to 9am against Chennai's window and left it there.
  const cityTimings =
    searchSelector?.cityTimings?.cityId === searchSelector?.city?.id
      ? searchSelector.cityTimings.days
      : null;
  const [hasMounted, setHasMounted] = useState(false);
  const [cityList, setCityList] = useState([]);
  const [selectedCity, setSelectedCity] = useState("Select City");

  const [selectedDuration, setSelectedDuration] = useState("Select Duration");

  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [citySearchQuery, setCitySearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [recentCities, setRecentCities] = useState([]);
  const cityWrapperRef = useRef(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [searchResult, setSelectedResult] = useState(false);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectValidatn, setSelectedValidatn] = useState("Please select city");
  const [showError, setShowError] = useState(false);
  const [isStartPickerOpen, setIsStartPickerOpen] = useState(false);
  const [isEndPickerOpen, setIsEndPickerOpen] = useState(false);
  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [emptyVehicle, setEmptyVehicle] = useState(false);
  const [showDurationList, setShowDurationList] = useState(false);

  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationMessage, setLocationMessage] = useState(null);

  // Hydration-safe: only read persisted Redux values after mount
  const cityName = hasMounted ? (searchSelector.city?.name || "Select City") : "Select City";


  // Sync persisted Redux state after hydration to avoid mismatch (mount-only)
  useEffect(() => {
    setHasMounted(true);
    if (searchSelector.city?.name) setSelectedCity(searchSelector.city.name);
    if (searchSelector.duration?.title) setSelectedDuration(searchSelector.duration.title);
    if (searchSelector.startDateTime?.date) setSelectedStartDate(searchSelector.startDateTime.date);
    if (searchSelector.endDateTime?.date) setSelectedEndDate(searchSelector.endDateTime.date);
    if (searchSelector.startDateTime?.time) setSelectedStartTime(searchSelector.startDateTime.time);
    if (searchSelector.endDateTime?.time) setSelectedEndTime(searchSelector.endDateTime.time);
    try {
      const recent = JSON.parse(localStorage.getItem('recentCities') || '[]');
      setRecentCities(recent);
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep local end date in sync when Redux endDateTime changes (e.g. Quick Duration tabs)
  useEffect(() => {
    if (!hasMounted) return;
    const reduxEndDate = searchSelector.endDateTime?.date;
    const reduxEndTime = searchSelector.endDateTime?.time;
    if (reduxEndDate && reduxEndDate !== selectedEndDate) {
      setSelectedEndDate(reduxEndDate);
    }
    if (reduxEndTime && reduxEndTime !== selectedEndTime) {
      setSelectedEndTime(reduxEndTime);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSelector.endDateTime?.date, searchSelector.endDateTime?.time]);

  useEffect(() => {
    const closeDropdown = (e) => {
      if (!showHideRef.current?.contains(e.target)) {
        setShowCityDropdown(false);
        setCitySearchQuery("");
        setHighlightedIndex(-1);
        setIsStartPickerOpen(false);
        setIsEndPickerOpen(false);
        setShowDurationList(false);
      }
    };
    document.body.addEventListener("click", closeDropdown);
    return () => document.body.removeEventListener("click", closeDropdown);
  }, [showHideRef]);

  const autoSetDates = useCallback(() => {
    const start = roundUpToSlotBoundary(new Date());
    const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
    const startDateStr = dayjs(start).format("YYYY-MM-DD");
    const startTimeStr = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
    const endDateStr = dayjs(end).format("YYYY-MM-DD");
    const endTimeStr = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
    setSelectedStartDate(startDateStr);
    setSelectedStartTime(startTimeStr);
    setSelectedEndDate(endDateStr);
    setSelectedEndTime(endTimeStr);
    dispatch(getStartDate({ date: startDateStr, time: startTimeStr }));
    dispatch(getEndDate({ date: endDateStr, time: endTimeStr }));
  }, [dispatch]);

  const dropoffMinDate = useMemo(() => {
    if (selectedStartDate && selectedStartTime) {
      const d = new Date(`${selectedStartDate}T${selectedStartTime}`);
      d.setHours(d.getHours() + 1);
      return d;
    }
    if (selectedStartDate) {
      const d = new Date(selectedStartDate);
      d.setHours(d.getHours() + 1);
      return d;
    }
    return new Date();
  }, [selectedStartDate, selectedStartTime]);

  const maxDate365 = useMemo(() => new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), []);

  // The pickup this effect last saw. Moving the pickup past the drop-off used to
  // collapse the booking to the 1-hour minimum, silently turning a 2-day rental
  // into an hour; measuring against the previous pickup keeps the length instead.
  const prevStartRef = useRef(null);

  // A pickup outside the city's opening hours sends the customer to a search page
  // with nothing bookable — Chennai closes at 21:00, so an 11pm visitor was being
  // pre-filled with 11pm. Once the city's real hours land, move the pickup to the
  // first slot that city can serve and carry the drop-off with it so the chosen
  // duration survives. Fires only when the current pickup is genuinely unbookable.
  useEffect(() => {
    if (!cityTimings) return;
    if (!selectedStartDate || !selectedStartTime) return;
    if (!selectedEndDate || !selectedEndTime) return;

    const rolled = rollStaleStartForward(
      { date: selectedStartDate, time: selectedStartTime },
      { date: selectedEndDate, time: selectedEndTime },
      cityTimings
    );
    if (!rolled) return;

    setSelectedStartDate(rolled.startDate);
    setSelectedStartTime(rolled.startTime);
    setSelectedEndDate(rolled.endDate);
    setSelectedEndTime(rolled.endTime);
    dispatch(getStartDate({ date: rolled.startDate, time: rolled.startTime }));
    dispatch(getEndDate({ date: rolled.endDate, time: rolled.endTime }));
    toast.info(`Pickup moved to ${rolled.label} — the first time partners here are open`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityTimings, selectedStartDate, selectedStartTime]);

  useEffect(() => {
    if (!selectedStartDate || !selectedStartTime) return;
    const startDT = new Date(`${selectedStartDate}T${selectedStartTime}`);
    if (Number.isNaN(startDT.getTime())) return;
    const prevStart = prevStartRef.current;
    prevStartRef.current = startDT;

    if (!selectedEndDate || !selectedEndTime) return;
    const endDT = new Date(`${selectedEndDate}T${selectedEndTime}`);
    if (Number.isNaN(endDT.getTime())) return;
    if (endDT > startDT) return;

    const ONE_HOUR = 60 * 60 * 1000;
    const held = prevStart && endDT > prevStart ? endDT.getTime() - prevStart.getTime() : 0;
    let adjusted = new Date(startDT.getTime() + Math.max(held, ONE_HOUR));
    // Never push the drop-off past the window the picker itself allows.
    if (adjusted > maxDate365) adjusted = new Date(startDT.getTime() + ONE_HOUR);
    const adjDateStr = dayjs(adjusted).format("YYYY-MM-DD");
    const adjTimeStr = `${String(adjusted.getHours()).padStart(2, '0')}:${String(adjusted.getMinutes()).padStart(2, '0')}`;
    setSelectedEndDate(adjDateStr);
    setSelectedEndTime(adjTimeStr);
    dispatch(getEndDate({ date: adjDateStr, time: adjTimeStr }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate, selectedStartTime]);

  const handleStartDateClick = () => {
    if (!searchSelector.city?.id) {
      toast.warning('Please select a city first');
      return;
    }
    setIsStartPickerOpen(true);
    setIsEndPickerOpen(false);
    setShowCityDropdown(false);
    setCitySearchQuery("");
    setHighlightedIndex(-1);
    setShowDurationList(false);
  };

  const handleEndDateClick = () => {
    if (!searchSelector.city?.id) {
      toast.warning('Please select a city first');
      return;
    }
    if (!selectedStartDate) {
      toast.warning('Please select pickup date first');
      return;
    }
    setIsEndPickerOpen(true);
    setIsStartPickerOpen(false);
    setShowCityDropdown(false);
    setCitySearchQuery("");
    setHighlightedIndex(-1);
    setShowDurationList(false);
  };

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <strong className="text-primary font-bold">{text.slice(idx, idx + query.length)}</strong>
        {text.slice(idx + query.length)}
      </>
    );
  };

  const getFilteredCities = useCallback(() => {
    return cityList?.filter((city) =>
      city.name.toLowerCase().includes(citySearchQuery.toLowerCase())
    ) || [];
  }, [cityList, citySearchQuery]);

  const handleDropdownKeyDown = useCallback((e) => {
    const filtered = getFilteredCities();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(filtered.length - 1, prev + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(0, prev - 1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < filtered.length) {
      e.preventDefault();
      setCitySearchQuery("");
      setHighlightedIndex(-1);
      handleCitySelectionInternal(filtered[highlightedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowCityDropdown(false);
      setCitySearchQuery("");
      setHighlightedIndex(-1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFilteredCities, highlightedIndex]);

  useEffect(() => {
    if (highlightedIndex >= 0 && cityWrapperRef.current) {
      const items = cityWrapperRef.current.querySelectorAll('[data-city-option]');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [citySearchQuery]);

  const handleCitySelectionInternal = (city) => {
    setSelectedCity(city.name);
    if (searchSelector.city.id == city.id) {
      dispatch(setCityname({ id: city.id, name: city.name }));
    } else {
      dispatch(setCityname({ id: city.id, name: city.name }));
      dispatch(setEmptyCartData(true));
    }

    setLocationMessage(null);

    try {
      const recent = JSON.parse(localStorage.getItem('recentCities') || '[]');
      const updated = [{ id: city.id, name: city.name }, ...recent.filter(c => c.id !== city.id)].slice(0, 3);
      localStorage.setItem('recentCities', JSON.stringify(updated));
      setRecentCities(updated);
    } catch {}

    setIsCityDropdownOpen(!isCityDropdownOpen);
    setShowCityDropdown(!showCityDropdown);

    autoSetDates();
  };

  const handleCitySelection = handleCitySelectionInternal;

  // Partner-storefront guard shared by mount-time detection and the locate
  // button: global IP/GPS detection can select — or list — cities the partner
  // does not serve, stranding the customer in an empty scoped search. A
  // single-city partner gets that city preselected; multi-city opens the
  // scoped picker. Returns true when the partner scope handled selection.
  const applyPartnerCityScope = useCallback(() => {
    const scoped = partnerCityList(partner);
    if (!scoped || !scoped.length) return false;
    const currentId = searchSelector.city?.id;
    if (currentId && scoped.some((c) => String(c.id) === String(currentId))) {
      return true;
    }
    if (scoped.length === 1) {
      setSelectedCity(scoped[0].name);
      dispatch(setCityname({ id: scoped[0].id, name: scoped[0].name }));
      autoSetDates();
    } else {
      setCityList(scoped);
      setShowCityDropdown(true);
    }
    return true;
  }, [partner, searchSelector, dispatch, autoSetDates]);

  const handleDetectLocation = useCallback(async () => {
    if (applyPartnerCityScope()) return;
    setIsDetectingLocation(true);
    setLocationMessage(null);
    setShowCityDropdown(false);

    try {
      const filters = {};

      if (pathname === "/monthly-bike-rental") {
        filters.longRentStatus = 1;
      }

      try {
        const coords = await getCurrentPosition();
        const response = await detectCityByCoordinates(coords.lat, coords.lng, filters);

        if (response.status === 1 && response.data) {
          setSelectedCity(response.data.name);
          if (searchSelector.city.id == response.data.id) {
            dispatch(setCityname({ id: response.data.id, name: response.data.name }));
          } else {
            dispatch(setCityname({ id: response.data.id, name: response.data.name }));
            dispatch(setEmptyCartData(true));
          }
          setLocationMessage(null);
          toast.success('Location detected successfully');
          autoSetDates();
          setIsDetectingLocation(false);
          return;
        }
      } catch (gpsError) {
        if (gpsError.message.includes('denied')) {

          let apiUrl = "";
          if (pathname === "/monthly-bike-rental") {
            apiUrl = "data/cities?longRentStatus=1";
          } else {
            apiUrl = "data/cities";
          }

          try {
            const cityResponse = await usefetchDataFromApi({
              url: apiUrl,
              method: "POST",
            });

            if (cityResponse.success) {
              setCityList(cityResponse.data?.data);
              setShowCityDropdown(true);
            }
          } catch (err) {
          }

          setIsDetectingLocation(false);
          return;
        }
      }

      const ipResponse = await detectCityByIP(filters);

      if (ipResponse.status === 1 && ipResponse.data) {
        setSelectedCity(ipResponse.data.name);
        if (searchSelector.city.id == ipResponse.data.id) {
          dispatch(setCityname({ id: ipResponse.data.id, name: ipResponse.data.name }));
        } else {
          dispatch(setCityname({ id: ipResponse.data.id, name: ipResponse.data.name }));
          dispatch(setEmptyCartData(true));
        }
        setLocationMessage(null);
        toast.info('Location detected from IP (approximate)');
        autoSetDates();
      } else {
        setLocationMessage('⚠️ Please select your city');
        toast.warning('Unable to detect location. Please select your city.');

        let apiUrl = "";
        if (pathname === "/monthly-bike-rental") {
          apiUrl = "data/cities?longRentStatus=1";
        } else {
          apiUrl = "data/cities";
        }

        try {
          const cityResponse = await usefetchDataFromApi({
            url: apiUrl,
            method: "POST",
          });

          if (cityResponse.success) {
            setCityList(cityResponse.data?.data);
            setShowCityDropdown(true);
          }
        } catch (err) {
        }
      }
    } catch (error) {
      setLocationMessage('⚠️ Please select your city');
      toast.error('Failed to detect location. Please select your city.');

      let apiUrl = "";
      if (pathname === "/monthly-bike-rental") {
        apiUrl = "data/cities?longRentStatus=1";
      } else {
        apiUrl = "data/cities";
      }

      try {
        const cityResponse = await usefetchDataFromApi({
          url: apiUrl,
          method: "POST",
        });

        if (cityResponse.success) {
          setCityList(cityResponse.data?.data);
          setShowCityDropdown(true);
        }
      } catch (err) {
      }
    } finally {
      setIsDetectingLocation(false);
    }
  }, [pathname, searchSelector, dispatch, autoSetDates, applyPartnerCityScope]);

  // City detection on mount, in two phases:
  //   1. Silent IP detection — no permission prompt, runs immediately.
  //   2. GPS fallback — only if IP fails. Deferred until `window.load` (full
  //      page-load complete) + an idle window, so the prompt doesn't compete
  //      with hydration or LCP. This will trip Lighthouse's "geolocation on
  //      page load" Best Practices audit — that is an accepted trade-off for
  //      automatic city detection.
  useEffect(() => {
    if (window.innerWidth < 1024) return;

    // On a city/locality SEO page, pre-fill that page's city (intent match) and
    // skip geo. Id mapping resolved in HeroSection. Not for /monthly-bike-rental*.
    if (seoCity?.id && !(pathname || "").includes("monthly")) {
      if (searchSelector.city?.id !== seoCity.id) {
        dispatch(setCityname({ id: seoCity.id, name: seoCity.name }));
        autoSetDates();
      }
      return;
    }

    // Partner storefront: the scope decides the city; skip geo-detection.
    if (applyPartnerCityScope()) return;

    if (searchSelector.city?.id && searchSelector.city?.name !== "Select City") return;

    let cancelled = false;
    let idleHandle = null;
    let timeoutHandle = null;
    let loadListener = null;

    const fireGps = () => {
      if (cancelled) return;
      handleDetectLocation();
    };

    const scheduleAfterIdle = () => {
      if (cancelled) return;
      if (typeof window.requestIdleCallback === "function") {
        idleHandle = window.requestIdleCallback(fireGps, { timeout: 1500 });
      } else {
        timeoutHandle = window.setTimeout(fireGps, 600);
      }
    };

    const scheduleAfterLoad = () => {
      if (document.readyState === "complete") {
        scheduleAfterIdle();
      } else {
        loadListener = () => scheduleAfterIdle();
        window.addEventListener("load", loadListener, { once: true });
      }
    };

    const tryIpThenScheduleGps = async () => {
      try {
        setIsDetectingLocation(true);
        const filters = {};
        if (pathname === "/monthly-bike-rental") filters.longRentStatus = 1;
        const ipResponse = await detectCityByIP(filters);
        if (cancelled) return;
        if (ipResponse.status === 1 && ipResponse.data) {
          dispatch(setCityname({ id: ipResponse.data.id, name: ipResponse.data.name }));
          return;
        }
      } catch (e) {
        // fall through to GPS-after-load
      } finally {
        if (!cancelled) setIsDetectingLocation(false);
      }
      if (!cancelled) scheduleAfterLoad();
    };

    tryIpThenScheduleGps();

    return () => {
      cancelled = true;
      if (loadListener) window.removeEventListener("load", loadListener);
      if (idleHandle != null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle != null) window.clearTimeout(timeoutHandle);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDuratnSelect = (data) => {
    setSelectedDuration(data.value);
    if (searchSelector.duration.id == data.id) {
      dispatch(
        setDuration({ id: data.id, value: data.value, title: data.title })
      );
    } else {
      dispatch(
        setDuration({ id: data.id, value: data.value, title: data.title })
      );
      dispatch(setEmptyCartData(true));
    }
    setShowDurationList(!showDurationList);
  };

  const handleSearch = useCallback(() => {
    let apiData = {};
    const {
      startDateTime,
      endDateTime,
      vehicleType,
      city,
    } = searchSelector;
    setShowError(false);
    setSelectedValidatn("");
    let startDTime = `${startDateTime.date} ${startDateTime.time}`;
    let endDTime = `${endDateTime.date} ${endDateTime.time}`;

    const rolled = rollStaleStartForward(startDateTime, endDateTime, cityTimings);
    if (rolled) {
      ({ startDTime, endDTime } = rolled);
      setSelectedStartDate(rolled.startDate);
      setSelectedStartTime(rolled.startTime);
      setSelectedEndDate(rolled.endDate);
      setSelectedEndTime(rolled.endTime);
      dispatch(getStartDate({ date: rolled.startDate, time: rolled.startTime }));
      dispatch(getEndDate({ date: rolled.endDate, time: rolled.endTime }));
      toast.info(`Pickup moved to ${rolled.label} — the earlier slot is no longer available`);
    }

    if (
      startDateTime?.date != "" &&
      startDateTime?.time != "" &&
      endDateTime?.date != "" &&
      endDateTime?.time != "" &&
      city?.name != "" &&
      vehicleType?.length != 0
    ) {
      setShowError(false);
      setSelectedResult(true);
      apiData = {
        types: vehicleType.filter((preState) => preState != "bike"),
        start: startDTime,
        end: endDTime,
        cityId: city.id,
        platform: 3,
      };
      apiData = withPartnerFilter(apiData, partner?.id);
      // Persist the checkout origin: on a partner storefront this writes the
      // partner-origin cookie, on the global site it clears any stale one, so
      // the later /cart booking is attributed to the correct channel.
      syncPartnerBookingOrigin(partner);
      usefetchDataFromApi({
        url: "search/index",
        method: "POST",
        data: apiData,
      })
        .then((res) => {
          if (res.success) {
            const { vehicles, filters } = res.data?.data || {};
            dispatch(
              getTempDateTime({
                sDateTime: startDTime,
                eDateTime: endDTime,
                tCityId: city.id,
              })
            );
            vehicles?.length == 0 && setEmptyVehicle(true);
            let finalVehicleList = getFinalVehicleList(
              vehicles,
              startDTime,
              endDTime
            );

            dispatch(getAllVehicles(finalVehicleList));
            // Every facet list, not just areas — a search into a different city or
            // vehicle type leaves the brand/fuel/transmission options of the previous
            // one on screen otherwise.
            dispatch(setSearchAreas(filters?.areas || []));
            dispatch(setSearchBrands(filters?.brands || []));
            dispatch(setSearchFuelTypes(filters?.fuelTypes || []));
            dispatch(setSearchTransmissions(filters?.transmissions || []));
            // ...and every selection. This request carries no filters or sort, so any
            // selection left in the store would show as an active pill over results
            // that ignore it.
            dispatch(setSelectedAreas([]));
            dispatch(setSelectedBrands([]));
            dispatch(setSelectedFuelTypes([]));
            dispatch(setSelectedTransmissions([]));
            dispatch(setSortBy(""));
            setSelectedResult(false);
            if (finalVehicleList.length == 0) {
              setEmptyVehicle(true);
            } else {
              if (!pathname.includes("monthly-bike-rental-in-")) {
                dispatch(setSearchType(pathname));
              }
              router.push(
                partner?.slug ? `/partner/${partner.slug}/search` : "/search"
              );
            }
          } else {
            setShowError(true);
            setSelectedResult(false);
            setSelectedValidatn(res.message);
          }
        })
        .catch((err) => {
          setSelectedResult(false);
          setSelectedValidatn(handleApiError(err));
        });
    } else {
      if (vehicleType.length == 0) {
        setSelectedValidatn("Please choose vehicles type.");
      } else if (city?.name == "") {
        setSelectedValidatn("Please select city");
      } else if (startDateTime?.date == "" || startDateTime?.time == "") {
        setSelectedValidatn("Please select start date and time");
      } else if (endDateTime?.date == "" || endDateTime?.time == "") {
        setSelectedValidatn("Please select end date and time");
      }
      setShowError(true);
    }
  }, [dispatch, pathname, router, searchSelector, partner]);

  const handleEmptyCart = useCallback(() => {
    usefetchDataFromApi({
      url: "cart/cart-empty",
      method: "POST",
    })
      .then((res) => {
        if (res.success) {
          dispatch(bumpCartVersion());
          dispatch(
            getTempDateTime({
              sDateTime: "",
              eDateTime: "",
              tCityId: "",
            })
          );
          handleSearch();
        }
      })
      .catch(() => {
      });
  }, [dispatch, handleSearch]);

  const handleEmptySearch = useCallback(() => {
    const {
      startDateTime,
      endDateTime,
      tempStartDateTime,
      tempEndDateTime,
      tempCityId,
      city,
    } = searchSelector;
    let startDTime = `${startDateTime.date} ${startDateTime.time}`;
    let endDTime = `${endDateTime.date} ${endDateTime.time}`;

    if (tempStartDateTime != "" && tempEndDateTime != "" && tempCityId != "") {
      if (
        startDTime == tempStartDateTime &&
        endDTime == tempEndDateTime &&
        city.id == tempCityId
      ) {
        handleSearch();
      } else {
        handleEmptyCart();
      }
    } else {
      handleSearch();
    }
  }, [handleSearch, handleEmptyCart, searchSelector]);

  const handleCityList = useCallback((searchPlace) => {
    setShowError(false);
    setSelectedValidatn("");
    setIsStartPickerOpen(false);
    setIsEndPickerOpen(false);
    setShowDurationList(false);
    // On a partner storefront the picker is scoped to the partner's operating
    // cities (carried on the partner profile), so skip the global data/cities
    // fetch entirely. Off-storefront (partner null) this is a no-op.
    const scopedCities = partnerCityList(partner);
    if (scopedCities) {
      if (scopedCities.length === 0) {
        setShowCityDropdown(false);
        setShowError(true);
        setSelectedValidatn("City List not found.");
        return;
      }
      setShowCityDropdown((prev) => !prev);
      setCityList(scopedCities);
      return;
    }
    const { searchType } = searchSelector;
    if (searchPlace == "topSearch") {
      let apiUrl = "";
      if (searchType == "/monthly-bike-rental") {
        apiUrl = "data/cities?longRentStatus=1";
      } else {
        apiUrl = "data/cities";
      }
      usefetchDataFromApi({
        url: apiUrl,
        method: "POST",
      })
        .then((res) => {
          if (res.success) {
            const cities = res.data?.data;
            if (cities?.length == 0) {
              setShowCityDropdown(false);
              setShowError(true);
              setSelectedValidatn("City List not found.");
            }
            setShowCityDropdown(!showCityDropdown);
            setCityList(cities);
          } else {
            setShowCityDropdown(false);
            setShowError(true);
            setSelectedValidatn("City List not found.");
          }
        })
        .catch((err) => {
          setShowCityDropdown(false);
          setShowError(true);
          setSelectedValidatn(handleApiError(err));
        });
    } else {
      let apiUrl = "";
      if (searchType == "/monthly-bike-rental") {
        apiUrl = "data/cities?longRentStatus=1";
      } else {
        apiUrl = "data/cities";
      }
      usefetchDataFromApi({
        url: apiUrl,
        method: "POST",
      })
        .then((res) => {
          if (res.success) {
            const cities = res.data?.data;
            if (cities?.length == 0) {
              setShowCityDropdown(false);
              setShowError(true);
              setSelectedValidatn("City List not found.");
            }
            setShowCityDropdown(!showCityDropdown);
            setCityList(cities);
          } else {
            setShowCityDropdown(false);
            setShowError(true);
            setSelectedValidatn("City List not found.");
          }
        })
        .catch((err) => {
          setShowCityDropdown(false);
          setShowError(true);
          setSelectedValidatn(handleApiError(err));
        });
    }
  }, [searchSelector, showCityDropdown, partner]);

  const handleEndDSelect = useCallback(() => {
    const { duration } = searchSelector;
    const duratnArr = duration?.value.split(" ");
    const a = dayjs(searchSelector?.startDateTime?.date);
    const b = a.add(Number(duratnArr[0]), String(duratnArr[1]));
    if (b.format("YYYY-MM-DD") != "Invalid Date") {
      dispatch(getEndDate({ date: b.format("YYYY-MM-DD") }));
      dispatch(getEndDate({ time: searchSelector?.startDateTime?.time }));
    }
  }, [dispatch, searchSelector]);

  useEffect(() => {
    if (pathname.includes("/monthly-bike-rental-in-")) {
      handleEndDSelect();
    }
  }, [
    pathname,
    searchSelector?.startDateTime?.date,
    searchSelector?.startDateTime?.time,
    selectedDuration,
    handleEndDSelect,
  ]);




  return (
    <>
      <div className={`m-0 hidden lg:block relative ${type === "header" ? "w-full max-w-[560px]" : "max-w-[1080px] w-[70%]"}`} ref={showHideRef}>
        <div className={`flex items-center w-full bg-white border border-black/[0.08] rounded-search shadow-search overflow-visible ${type === "header" ? "py-0.5 pl-2.5 h-[44px]" : "pl-5"}`}>
          <div className={`text-left cursor-pointer relative ${type === "header" ? "flex-[unset] py-[5px] px-1 ml-0" : "flex-1 py-[5px] px-[7px] ml-5"}`}>
          {type === "header" ? (
                <div
                  className="text-[16px] text-textMutedSoft font-medium flex items-center gap-2"
                  onClick={() => handleCityList("topSearch")}
                >
                  {isDetectingLocation ? "Detecting..." : cityName}
                  {!isDetectingLocation && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDetectLocation();
                      }}
                      className="ml-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                      title="Detect my location"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-primary"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
                      </svg>
                    </button>
                  )}
                </div>
              ) : (
                <div onClick={() => handleCityList("normalSearch")} className="flex items-center justify-between w-full">
                  <div>
                    <div className="text-xs text-textMuted font-medium uppercase tracking-[0.5px]">City</div>
                    <div className="text-[15px] text-textPrimary font-semibold">{isDetectingLocation ? "Detecting..." : cityName}</div>
                  </div>
                  {!isDetectingLocation && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDetectLocation();
                      }}
                      className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                      title="Detect my location"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-primary"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
                      </svg>
                    </button>
                  )}
                </div>
              )}

            {showCityDropdown && (
              <div className="absolute top-[calc(100%+8px)] left-0 bg-white rounded-2xl z-50 min-w-[200px] max-w-[220px] w-full py-2 shadow-dropdown animate-[dropdownFadeIn_0.2s_ease-out] origin-top" onKeyDown={handleDropdownKeyDown}>
                {cityList?.length > 5 && (
                  <div className="px-3 pt-2 pb-1 border-b border-gray-100 mb-1">
                    <input
                      type="text"
                      className="w-full py-2 px-2.5 pl-8 text-[13px] font-normal text-textPrimary border border-gray-200 rounded-card outline-none bg-gray-50 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23999%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Ccircle%20cx%3D%2211%22%20cy%3D%2211%22%20r%3D%228%22%2F%3E%3Cline%20x1%3D%2221%22%20y1%3D%2221%22%20x2%3D%2216.65%22%20y2%3D%2216.65%22%2F%3E%3C%2Fsvg%3E')] bg-[10px_center] bg-no-repeat placeholder:text-textPlaceholder focus:border-primary focus:shadow-ring-soft"
                      placeholder="Search city..."
                      value={citySearchQuery}
                      onChange={(e) => setCitySearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={handleDropdownKeyDown}
                      autoFocus
                    />
                  </div>
                )}

                {!citySearchQuery && recentCities.length > 0 && (
                  <>
                    <div className="px-4 pt-1.5 pb-0.5 text-[10px] font-semibold tracking-[0.06em] uppercase text-textMuted">Recent</div>
                    <div className="border-b border-gray-100 pb-1 mb-1">
                      {recentCities
                        .filter(rc => cityList?.some(c => c.id === rc.id))
                        .map((city) => (
                          <div
                            key={`recent-${city.id}`}
                            className={`flex items-center gap-2.5 py-2 px-4 cursor-pointer text-[13px] font-medium text-gray-500 border-l-[3px] border-transparent transition-[background-color,padding-left,border-color] duration-150 hover:bg-red-50 hover:pl-5 hover:border-l-primary ${selectedCity === city.id ? "!bg-red-50 !border-l-primary !text-primary" : ""}`}
                            onClick={() => {
                              setCitySearchQuery("");
                              setHighlightedIndex(-1);
                              handleCitySelection(city);
                            }}
                          >
                            <svg className="shrink-0 w-3.5 h-3.5 text-textMuted transition-colors duration-150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                            </svg>
                            {city.name}
                          </div>
                        ))}
                    </div>
                  </>
                )}

                {(() => { const fc = getFilteredCities(); return (
                  <div className="px-4 pt-1.5 pb-0.5 text-[10px] font-semibold tracking-[0.06em] uppercase text-textMuted">
                    Popular Cities
                    <span className="ml-1.5 text-[10px] font-medium text-textMuted tracking-normal normal-case">{fc.length} {fc.length === 1 ? 'city' : 'cities'}</span>
                  </div>
                ); })()}

                <div className="overflow-y-auto max-h-60 scrollbar-thin [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-black/15 [&::-webkit-scrollbar-thumb]:rounded-card [&::-webkit-scrollbar-thumb:hover]:bg-black/25 [&::-webkit-scrollbar-track]:bg-transparent" ref={cityWrapperRef}>

                  {cityList?.length === 0 ? (
                    <div className="py-1">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="flex items-center gap-2.5 py-2.5 px-4">
                          <div className="w-4 h-4 rounded-full bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200px_100%] animate-[citySkeletonShimmer_1.2s_ease-in-out_infinite]" />
                          <div className="h-3 rounded-md bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200px_100%] animate-[citySkeletonShimmer_1.2s_ease-in-out_infinite]" style={{ width: `${60 + Math.random() * 40}%` }} />
                        </div>
                      ))}
                    </div>
                  ) : getFilteredCities().length === 0 ? (
                    <div className="py-4 text-center text-[13px] text-textPlaceholder">No cities found</div>
                  ) : (
                    getFilteredCities().map((city, index) => (
                        <div
                          key={index}
                          data-city-option
                          className={`flex items-center gap-2.5 py-2.5 px-4 cursor-pointer text-sm font-medium text-textPrimary border-l-[3px] border-transparent transition-[background-color,padding-left,border-color] duration-150 hover:bg-red-50 hover:pl-5 hover:border-l-primary ${
                            selectedCity === city.id ? "!bg-red-50 !border-l-primary !text-primary" : ""
                          } ${highlightedIndex === index ? "!bg-red-50 !pl-5 !border-l-primary" : ""}`}
                          onClick={() => {
                            setCitySearchQuery("");
                            setHighlightedIndex(-1);
                            handleCitySelection(city);
                          }}
                          onMouseEnter={() => setHighlightedIndex(index)}
                        >
                          <svg className={`shrink-0 w-4 h-4 transition-colors duration-150 ${selectedCity === city.id || highlightedIndex === index ? "text-primary" : "text-textPlaceholder"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          <span className="flex-1 min-w-0">{highlightMatch(city.name, citySearchQuery)}</span>
                          <svg className={`ml-auto shrink-0 w-4 h-4 text-primary transition-[opacity,transform] duration-150 ${selectedCity === city.id ? "opacity-100 scale-100" : "opacity-0 scale-50"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                      ))
                  )}
                </div>
              </div>
            )}


            {locationMessage && !showCityDropdown && (
              <div className="absolute top-full left-0 mt-1 text-xs px-2">
                <span className={
                  locationMessage.startsWith('✅') ? 'text-green-600' :
                  locationMessage.startsWith('ℹ️') ? 'text-orange-600' :
                  'text-red-600'
                }>
                  {locationMessage}
                </span>
              </div>
            )}
          </div>
          <div className={`w-px ${type === "header" ? "h-7 bg-lineMid" : "h-12 bg-black/[0.1]"}`}></div>


          {type == "long-rental" && (
            <>
              <div
                className={`text-left cursor-pointer relative ${type === "header" ? "py-[5px] px-1 ml-0" : "flex-1 py-[5px] px-[7px] ml-5"}`}
                onClick={() => setShowDurationList(!showDurationList)}
              >
                {type == "header" ? (
                  <div
                    className={"text-[16px] text-textMutedSoft font-medium"}
                  >
                    {Object.keys(searchSelector.duration?.value).length == 0
                      ? "Duration"
                      : searchSelector.duration?.title}
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-textMuted font-medium uppercase tracking-[0.5px]">Duration </div>
                    <div className="text-[15px] text-textPrimary font-semibold">
                      {searchSelector?.duration?.title
                        ? searchSelector?.duration?.title
                        : "Select Duration"}
                    </div>
                  </>
                )}

                {showDurationList && (
                  <div className="absolute top-[110%] left-0 bg-white rounded-2xl z-[1] w-full shadow-[0_10px_40px_rgba(0,0,0,0.1),0_0_0_1px_rgba(255,90,95,0.05)] py-6">
                    <div className="overflow-y-auto max-h-60 scrollbar-thin [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-black/15 [&::-webkit-scrollbar-thumb]:rounded-card [&::-webkit-scrollbar-track]:bg-transparent">
                      {longTermDuration?.map((duratn, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-2.5 py-2.5 px-4 cursor-pointer text-sm font-medium text-textPrimary border-l-[3px] border-transparent transition-[background-color,padding-left,border-color] duration-150 hover:bg-red-50 hover:pl-5 hover:border-l-primary ${
                            selectedDuration === duratn.value ? "!bg-red-50 !border-l-primary !text-primary" : ""
                          }`}
                          onClick={() => handleDuratnSelect(duratn)}
                        >
                          {duratn.title}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className={`w-px ${type === "header" ? "h-7 bg-lineMid" : "h-12 bg-black/[0.1]"}`}></div>
            </>
          )}

          <div
            className={`flex-1 text-left cursor-pointer relative ${type === "header" ? "py-[5px] px-1 ml-0" : "py-[5px] px-[7px] ml-5"}`}
            onClick={() => handleStartDateClick()}
          >
           {type === "header" ? (
                <>
                  <div
                    className="text-[14px] text-textMutedSoft font-medium"
                  >
                    {showDateTime(selectedStartDate, selectedStartTime, "Start Date")}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-xs text-textMuted font-medium uppercase tracking-[0.5px]">Pickup</div>
                  <div className="text-[15px] text-textPrimary font-semibold">
                    {showDateTime(selectedStartDate, selectedStartTime)}
                  </div>
                </>
              )}

            {isStartPickerOpen && (
              <DateTimeCard
                label="Select Pickup"
                selectedDate={selectedStartDate}
                setSelectedDate={setSelectedStartDate}
                selectedTime={selectedStartTime}
                setSelectedTime={setSelectedStartTime}
                setIsPickerOpen={setIsStartPickerOpen}
                minDate={new Date()}
                maxDate={maxDate365}
                type="startDate"
                dayWindows={cityTimings}
              />
            )}
          </div>

          {type != "long-rental" && (
            <>
              <div className={`w-px ${type === "header" ? "h-7 bg-lineMid" : "h-12 bg-black/[0.1]"}`}></div>
              <div
                className={`flex-1 text-left cursor-pointer relative ${type === "header" ? "py-[5px] px-1 ml-0" : "py-[5px] px-[7px] ml-5"}`}
                onClick={() => handleEndDateClick()}
              >
               {type === "header" ? (
                  <>
                    <div
                      className="text-[14px] text-textMutedSoft font-medium"
                    >
                      {showDateTime(selectedEndDate, selectedEndTime, "End Date")}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-xs text-textMuted font-medium uppercase tracking-[0.5px]">Dropoff</div>
                    <div className="text-[15px] text-textPrimary font-semibold">
                      {showDateTime(selectedEndDate, selectedEndTime)}
                    </div>
                  </>
                )}

                {isEndPickerOpen && (
                  <DateTimeCard
                    label="Select Drop Off"
                    selectedDate={selectedEndDate}
                    setSelectedDate={setSelectedEndDate}
                    selectedTime={selectedEndTime}
                    setSelectedTime={setSelectedEndTime}
                    setIsPickerOpen={setIsEndPickerOpen}
                    minDate={dropoffMinDate}
                    maxDate={maxDate365}
                    type="endDate"
                    dayWindows={cityTimings}
                  />
                )}
              </div>
            </>
          )}

          {type == "header" ? (
              <div className="flex items-center justify-end p-0 ml-0 relative cursor-pointer">
                <button
                  disabled={searchResult}
                  className="rounded-full items-center justify-center align-center flex w-11 h-11 bg-primary text-white"
                  onClick={() => handleEmptySearch()}
                >
                  {searchResult ? (
                    <Image
                      alt=""
                      src={IMAGES.Spinner2}
                      width={16}
                      height={16}
                      className="w-4 h-4"
                      unoptimized
                    />
                  ) : (
                    <Image
                      alt=""
                      src={IMAGES.GuideSearch}
                      width={20}
                      height={20}
                      className="w-5 h-5"
                    />
                  )}
                </button>
              </div>
          ) : (
            <div className="flex-1 flex items-center justify-end py-[5px] px-[7px] text-left cursor-pointer ml-5 relative">
              <Button
                size="md"
                loading={searchResult}
                onClick={() => handleEmptySearch()}
                leftIcon={
                  <Image
                    alt=""
                    src={IMAGES.GuideSearch}
                    width={20}
                    height={20}
                    className="w-5 h-5"
                  />
                }
                className="w-[164px]"
              >
                Search
              </Button>
            </div>
          )}
        </div>
        {showError && (
          <p className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-50 text-red text-sm bg-gray-200 rounded-lg px-4 py-1 error-message whitespace-nowrap shadow-md">
            {selectValidatn}
          </p>
        )}
      </div>
    </>
  );
};

export default SearchFormDesktop;
